// Qué es un torneo o una categoría bien formados. Puro: lo usa la API para
// decidir, y la web para avisar antes de mandar. La palabra final es la API.

import { esTema, TEMA_POR_DEFECTO, type Tema } from './temas';
import { limpiar } from './texto';
import { validarOpciones, type OpcionesTorneo } from './opciones';

export type Resultado<T> = { ok: true; valor: T } | { ok: false; error: string };
export const bien = <T>(valor: T): Resultado<T> => ({ ok: true, valor });
export const mal = <T = never>(error: string): Resultado<T> => ({ ok: false, error });

function objeto(cuerpo: unknown): Record<string, unknown> {
  return typeof cuerpo === 'object' && cuerpo !== null ? (cuerpo as Record<string, unknown>) : {};
}

function texto(valor: unknown): string {
  return typeof valor === 'string' ? limpiar(valor) : '';
}

// ── Fechas ────────────────────────────────────────────────────────────────

/** "2026-10-17" y que exista de verdad (no 2026-02-30). */
export function fechaValida(valor: unknown): valor is string {
  if (typeof valor !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false;
  const [a, m, d] = valor.split('-').map(Number) as [number, number, number];
  const f = new Date(Date.UTC(a, m - 1, d));
  return f.getUTCFullYear() === a && f.getUTCMonth() === m - 1 && f.getUTCDate() === d;
}

// ── Torneo ────────────────────────────────────────────────────────────────

export const ESTADOS_TORNEO = ['borrador', 'publicado', 'terminado'] as const;
export type EstadoTorneo = (typeof ESTADOS_TORNEO)[number];

export interface DatosTorneo {
  opciones?: OpcionesTorneo;
  nombre: string;
  sede: string | null;
  fecha_desde: string;
  fecha_hasta: string;
  tema: Tema;
  /** Cuántas canchas tiene la sede. null = todavía no se dijo. */
  canchas: number | null;
}

export function validarTorneo(cuerpo: unknown): Resultado<DatosTorneo> {
  const c = objeto(cuerpo);
  const nombre = texto(c.nombre);
  if (nombre.length < 3) return mal('El nombre del torneo es muy corto.');
  if (nombre.length > 60) return mal('El nombre del torneo es muy largo (máx. 60).');

  const sede = texto(c.sede);
  if (sede.length > 60) return mal('La sede es muy larga (máx. 60).');

  if (!fechaValida(c.fecha_desde)) return mal('Falta la fecha de inicio.');
  if (!fechaValida(c.fecha_hasta)) return mal('Falta la fecha de fin.');
  if (c.fecha_hasta < c.fecha_desde) return mal('El torneo termina antes de empezar.');

  const tema = c.tema ?? TEMA_POR_DEFECTO;
  if (!esTema(tema)) return mal('Ese tema de color no existe.');

  const canchas = c.canchas ?? null;
  if (canchas !== null && (typeof canchas !== 'number' || !Number.isInteger(canchas) || canchas < 1 || canchas > 20)) {
    return mal('Las canchas van de 1 a 20.');
  }

  const opciones = c.opciones === undefined ? undefined : validarOpciones(c.opciones);
  if (opciones && !opciones.ok) return mal(opciones.error);
  return bien({ nombre, sede: sede || null, fecha_desde: c.fecha_desde, fecha_hasta: c.fecha_hasta, tema, canchas, ...(opciones?.ok ? { opciones: opciones.valor } : {}) });
}

export function esEstadoTorneo(valor: unknown): valor is EstadoTorneo {
  return typeof valor === 'string' && (ESTADOS_TORNEO as readonly string[]).includes(valor);
}

// ── Categoría ─────────────────────────────────────────────────────────────

export const FORMATOS = ['grupos', 'eliminacion'] as const;
export type Formato = (typeof FORMATOS)[number];

export const NOMBRE_FORMATO: Record<Formato, string> = {
  grupos: 'Grupos + cuadro',
  eliminacion: 'Solo eliminación',
};

/**
 * Cuántos pasan por grupo, como mucho. El tamaño de los grupos NO se fija acá:
 * lo elige el organizador al armarlos, según cuántas parejas se inscribieron
 * (decidido por Marcos el 2026-09-30). La columna tam_grupo queda en la base
 * sin usarse (las de la etapa 1 la tienen cargada) y las nuevas van con NULL.
 */
export const PASAN_MAX = 3;

export interface DatosCategoria {
  nombre: string;
  formato: Formato;
  tam_grupo: number | null;
  pasan_por_grupo: number | null;
  tema: Tema | null;
  /**
   * Cuántas parejas entran en la categoría. null = sin cupo. Es un CONTADOR
   * para el organizador: si se pasa, la app avisa pero deja seguir (decidido
   * por Marcos el 2026-10-01). El público no lo ve.
   */
  cupo: number | null;
}

export const CUPO_MAXIMO = 500;

export function validarCategoria(cuerpo: unknown): Resultado<DatosCategoria> {
  const c = objeto(cuerpo);
  const nombre = texto(c.nombre);
  if (nombre.length < 2) return mal('Falta el nombre de la categoría (ej: 5ta Caballeros).');
  if (nombre.length > 40) return mal('El nombre de la categoría es muy largo (máx. 40).');

  if (c.formato !== 'grupos' && c.formato !== 'eliminacion') return mal('Elegí el formato.');

  const tema = c.tema ?? null;
  if (tema !== null && !esTema(tema)) return mal('Ese tema de color no existe.');

  const cupo = c.cupo ?? null;
  if (cupo !== null && (typeof cupo !== 'number' || !Number.isInteger(cupo) || cupo < 2 || cupo > CUPO_MAXIMO)) {
    return mal(`El cupo va de 2 a ${CUPO_MAXIMO} parejas, o vacío para que no tenga.`);
  }

  if (c.formato === 'eliminacion') {
    return bien({ nombre, formato: 'eliminacion', tam_grupo: null, pasan_por_grupo: null, tema, cupo });
  }

  const pasan = c.pasan_por_grupo;
  if (typeof pasan !== 'number' || !Number.isInteger(pasan) || pasan < 1 || pasan > PASAN_MAX) {
    return mal(`Por grupo pasan entre 1 y ${PASAN_MAX}.`);
  }

  return bien({ nombre, formato: 'grupos', tam_grupo: null, pasan_por_grupo: pasan, tema, cupo });
}

// ── Organizador ───────────────────────────────────────────────────────────

export function validarNombrePersona(valor: unknown): Resultado<string> {
  const nombre = texto(valor);
  if (nombre.length < 2) return mal('El nombre es muy corto.');
  if (nombre.length > 30) return mal('El nombre es muy largo (máx. 30).');
  return bien(nombre);
}

export function validarNombreOrganizacion(valor: unknown): Resultado<string> {
  const nombre = texto(valor);
  if (nombre.length < 2) return mal('Falta el nombre del club u organización.');
  if (nombre.length > 60) return mal('El nombre es muy largo (máx. 60).');
  return bien(nombre);
}
